import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { siteInfo } from "@/mocks/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [query, setQuery] = useState("");
  const catRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    // 在当前页模拟搜索跳转到分类首页(演示交互)
    window.REACT_APP_NAVIGATE(`/category/xuangyi`);
  };

  const navLink = (active: boolean) =>
    `whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
      active
        ? "text-accent-300"
        : "text-foreground-300 hover:text-accent-200"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-950/90 backdrop-blur-md border-b border-background-200/60"
          : "bg-background-950/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between px-4 md:px-6">
        {/* 品牌 */}
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-spy-line text-lg" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-xl font-bold tracking-wide text-foreground-100">
            {siteInfo.name}
          </span>
        </Link>

        {/* 桌面导航 */}
        <nav className="hidden items-center gap-1 md:flex">
          <Link to="/" className={navLink(location.pathname === "/")}>
            首页
          </Link>

          {/* 分类下拉 */}
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-foreground-300 transition-colors hover:text-accent-200"
            >
              分类
              <i
                className={`ri-arrow-down-s-line transition-transform ${
                  catOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
            {catOpen && (
              <div className="absolute left-1/2 top-full mt-2 w-[560px] -translate-x-1/2 rounded-lg border border-background-200/70 bg-background-900 p-3 shadow-lg">
                <div className="grid grid-cols-3 gap-2">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="group flex items-center gap-3 rounded-md border border-transparent bg-background-100 px-3 py-3 transition-colors hover:border-accent-300/50 hover:bg-background-200"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500/15 text-primary-400 group-hover:bg-primary-500/25">
                        <i className={`${c.icon}`} aria-hidden="true"></i>
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-foreground-100">
                          {c.name}
                        </span>
                        <span className="block text-xs text-foreground-500">
                          {c.en}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a href="#updates" className={navLink(false)}>
            最新更新
          </a>
          <a href="#hot" className={navLink(false)}>
            热门推荐
          </a>
          <Link
            to="/faq"
            className={navLink(location.pathname === "/faq")}
          >
            常见问题
          </Link>
          <Link
            to="/about"
            className={navLink(location.pathname === "/about")}
          >
            关于我们
          </Link>
        </nav>

        {/* 搜索 + 移动按钮 */}
        <div className="flex items-center gap-3">
          <form
            onSubmit={submitSearch}
            className="hidden items-center rounded-full border border-background-300/60 bg-background-100 px-3 lg:flex"
          >
            <i
              className="ri-search-line text-sm text-foreground-500"
              aria-hidden="true"
            ></i>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索悬疑推理作品"
              className="w-40 bg-transparent px-2 py-2 text-sm text-foreground-100 outline-none placeholder:text-foreground-600"
            />
            <button
              type="submit"
              className="cursor-pointer text-xs font-medium text-accent-300 hover:text-accent-200"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-200 hover:bg-background-200 md:hidden"
            aria-label="打开菜单"
          >
            <i className="ri-menu-line text-xl" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {mobileOpen && (
        <div className="border-t border-background-200/60 bg-background-950/95 px-4 pb-4 pt-2 md:hidden">
          <div className="flex flex-col gap-1">
            <Link to="/" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-200 hover:bg-background-200">
              首页
            </Link>
            <div className="mt-1 grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-300 hover:bg-background-200"
                >
                  <i className={`${c.icon} text-primary-400`} aria-hidden="true"></i>
                  {c.name}
                </Link>
              ))}
            </div>
            <Link to="/faq" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-200 hover:bg-background-200">
              常见问题
            </Link>
            <Link to="/about" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-200 hover:bg-background-200">
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}